import React, { useMemo } from "react";

import { useNavigate, useSoilSightCurrency, useTranslation } from "utils";

import { AlertBanner } from "components";

import { calcUpliftPercent, formatUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { ForecastExplainerBanner } from "pages/app/scenario-onboarding/components/ForecastExplainerBanner";
import { HowMuchBetterBanner } from "pages/app/scenario-onboarding/components/HowMuchBetterBanner";
import { OnboardingCalculatingState } from "pages/app/scenario-onboarding/components/OnboardingCalculatingState";
import { OnboardingForecastChart } from "pages/app/scenario-onboarding/components/OnboardingForecastChart";
import { OnboardingLayout } from "pages/app/scenario-onboarding/components/OnboardingLayout";
import { ScenarioStatCard } from "pages/app/scenario-onboarding/components/ScenarioStatCard";
import { useApproxValue } from "pages/app/scenario-onboarding/hooks/useApproxValue";
import { useOnboardingOutcomeSummary } from "pages/app/scenario-onboarding/hooks/useOnboardingOutcomeSummary";
import { OnboardingForecastPoint, buildOnboardingForecastSeries } from "pages/app/scenario-onboarding/utils/onboardingForecast";

export const Step2BaselineForecastPage = () => {
  const { t } = useTranslation(["scenarioAnalysis", "global"]);
  const navigate = useNavigate();
  const { formatCmOutcome } = useSoilSightCurrency();
  const { outcomes, calcStatus } = useScenarioOnboarding();
  const { baselineYears, changeYears, upliftPct, liftClassification, baselineTotal, changeTotal, formatMargin } = useOnboardingOutcomeSummary();

  const isCalculating = calcStatus === "calculating";
  // Calculation failed or timed out — show "—" and let the user proceed.
  const isCalcFailed = calcStatus === "error" && !outcomes;

  const hasAdaptedForecast = changeYears.length > 0;
  // At or behind the baseline, and measured — a null uplift is unknown, not flat, so it claims nothing.
  const hasNoImprovement = upliftPct != null && (liftClassification === "negative" || liftClassification === "noChange");

  // Carried by each card's primary figure; the chip inherits it from the value beside it.
  const approx = useApproxValue();

  const forecastSeries = useMemo(() => buildOnboardingForecastSeries(baselineYears, changeYears), [baselineYears, changeYears]);

  // Per-year hover content: the gap between the curves differs by year.
  const getTooltipContent = (point: OnboardingForecastPoint): string | null => {
    const pointUpliftPct = point.changeCumulative == null ? null : calcUpliftPercent(point.baselineCumulative, point.changeCumulative);
    // No comparable uplift for this year — the baseline figure is still worth showing.
    if (pointUpliftPct == null) {
      return t("scenarioAnalysis:onboarding.marginPerHa", { value: formatCmOutcome(point.baselineCumulative) });
    }

    // The curves cross, so each year carries its own sign: a shortfall is a difference, not a potential.
    // The `range` placeholder name stays: every other locale is Crowdin-managed and still interpolates it.
    const upliftLabel = formatUpliftPercent(pointUpliftPct);
    return pointUpliftPct < 0
      ? t("scenarioAnalysis:onboarding.step2.differenceCallout", { range: upliftLabel })
      : t("scenarioAnalysis:onboarding.step2.potentialCallout", { range: upliftLabel });
  };

  return (
    <OnboardingLayout
      stepIndex={1}
      pageName="scenario-onboarding-step-2"
      title={isCalculating ? undefined : t("scenarioAnalysis:onboarding.step2.heading")}
      subtitle={isCalculating ? undefined : t("scenarioAnalysis:onboarding.step2.subheading")}
      footer={{
        onBack: () => navigate("/scenarios-onboarding/step-1"),
        ctaLabel: t("scenarioAnalysis:onboarding.step2.continueCta"),
        onCtaClick: () => navigate("/scenarios-onboarding/step-3"),
        ctaDisabled: isCalculating,
        ctaTestId: "onboarding-step2-continue-btn",
      }}
    >
      {isCalculating ? (
        <OnboardingCalculatingState />
      ) : (
        <div className="onboarding-step__content">
          <div className="onboarding-scenario-cards">
            <ScenarioStatCard
              dotColor="green"
              title={t("scenarioAnalysis:onboarding.baselineCard.title")}
              value={baselineTotal == null ? "—" : approx(formatMargin(baselineTotal))}
              caption={t("scenarioAnalysis:onboarding.baselineCard.caption")}
              dataTestId="onboarding-baseline-card"
            />
            {!hasAdaptedForecast && !isCalcFailed ? (
              <HowMuchBetterBanner />
            ) : (
              <ScenarioStatCard
                dotColor="blue"
                title={t("scenarioAnalysis:onboarding.adaptedCard.title")}
                value={changeTotal == null ? "—" : approx(formatMargin(changeTotal))}
                caption={t("scenarioAnalysis:onboarding.baselineCard.caption")}
                chipText={upliftPct == null ? undefined : formatUpliftPercent(upliftPct)}
                // No trend arrow when the scenario performs like the baseline.
                chipTrend={liftClassification === "negative" ? "down" : liftClassification === "noChange" ? undefined : "up"}
                dataTestId="onboarding-adapted-card"
              />
            )}
          </div>

          {!isCalcFailed && (
            <OnboardingForecastChart
              data={forecastSeries}
              changeLineStyle={hasAdaptedForecast ? "dashed" : "none"}
              changeTone={hasNoImprovement ? "behind" : "ahead"}
              getTooltipContent={getTooltipContent}
            />
          )}

          {hasAdaptedForecast &&
            !isCalcFailed &&
            (hasNoImprovement ? (
              <AlertBanner
                dataTestId="onboarding-no-improvement-banner"
                type="warning"
                iconName="tabler/alert-circle"
                iconSize={24}
                iconAlign="start"
                description={t("scenarioAnalysis:onboarding.step2.noImprovementBanner")}
              />
            ) : (
              <HowMuchBetterBanner />
            ))}

          <ForecastExplainerBanner />
        </div>
      )}
    </OnboardingLayout>
  );
};
